
import React, { useState, useMemo, useEffect } from 'react';
import { userWithdrawalService, WithdrawalListEntry } from '../../services/user.withdrawal.service';
import { userTradeService, UserTrade } from '../../services/user.trade.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import { Wallet, Info, ArrowUpRight, ShieldCheck, History, Landmark, CheckCircle2, AlertCircle, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

const Withdrawals: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();

  const [requests, setRequests] = useState<WithdrawalListEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Financial Stats
  const [financialStats, setFinancialStats] = useState({
    totalProfitTrade: 0,
    totalLosses: 0,
    totalApprovedWithdrawals: 0
  });

  // Pagination & Filter State
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const limit = 10;

  // Form State
  const [amount, setAmount] = useState('');
  const [btcAddress, setBtcAddress] = useState('');

  const withdrawalAllowance = useMemo(() => {
    return Math.max(0, financialStats.totalProfitTrade - financialStats.totalLosses - financialStats.totalApprovedWithdrawals);
  }, [financialStats]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // 1. Fetch Redemption Ledger (Paginated)
      const withdrawalsRes = await userWithdrawalService.listWithdrawals({
        page,
        limit,
        status: statusFilter || undefined
      });
      if (withdrawalsRes.success) {
        setRequests(withdrawalsRes.data);
      }

      // 2. Fetch all data for financial calculations
      // Fetching without limit to ensure absolute accuracy for totals
      const allTradesRes = await userTradeService.listTrades({});
      const allWithdrawalsRes = await userWithdrawalService.listWithdrawals({});

      if (allTradesRes.success && allWithdrawalsRes.success) {
        const wins = allTradesRes.data
          .filter(t => t.outcome === 'PROFIT' || t.outcome === 'DEPOSIT')
          .reduce((sum, t) => sum + parseFloat(t.payout || '0'), 0);

        const losses = allTradesRes.data
          .filter(t => t.outcome === 'LOSS')
          .reduce((sum, t) => sum + parseFloat(t.principal || '0'), 0);

        const approvedWd = allWithdrawalsRes.data
          .filter(w => w.status === 'APPROVED')
          .reduce((sum, w) => sum + parseFloat(w.amount || '0'), 0);

        setFinancialStats({
          totalProfitTrade: wins,
          totalLosses: losses,
          totalApprovedWithdrawals: approvedWd
        });
      }

    } catch (error) {
      showToast('Nexus Timeout: Failed to sync redemption ledger', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, statusFilter]);

  const validateBTC = (addr: string) => {
    const regex = /^(1|3|bc1)[a-zA-HJ-NP-Z0-9]{25,62}$/;
    return regex.test(addr);
  };

  const isFormValid = useMemo(() => {
    const numAmount = parseFloat(amount);
    return (
      !isNaN(numAmount) &&
      numAmount > 0 &&
      numAmount <= withdrawalAllowance &&
      validateBTC(btcAddress) &&
      !!user?.walletId
    );
  }, [amount, btcAddress, withdrawalAllowance, user?.walletId]);

  const handleWithdrawal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid || !user?.walletId) return;

    setIsSubmitting(true);
    try {
      const response = await userWithdrawalService.createWithdrawal({
        walletId: user.walletId,
        amount: amount,
        destinationAddress: btcAddress
      });

      if (response.success) {
        showToast('Capital redemption request initiated successfully.', 'success');
        setSuccessMessage('Redemption request committed to blockchain gateway.');
        setAmount('');
        setBtcAddress('');
        setPage(1); // Reset to first page to see the new request
        fetchData();
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        showToast('Execution Rejected: Insufficient balance or invalid wallet.', 'error');
      }
    } catch (error) {
      showToast('Protocol Timeout: Redemption execution failed', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const maskAddress = (addr: string) => {
    if (!addr || addr.length < 10) return addr;
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED': return 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20';
      case 'REJECTED': return 'text-red-500 bg-red-500/10 border-red-500/20';
      case 'PENDING': return 'text-brand-primary bg-brand-primary/10 border-brand-primary/20';
      default: return 'text-brand-muted bg-brand-border';
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700 pb-20">

      {/* 1. Wallet Summary Section */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* 
        <div className="bg-brand-card border border-brand-border p-8 rounded-3xl relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
            <Landmark size={80} className="text-brand-primary" />
          </div>
          <div className="relative z-10">
            <p className="text-brand-muted text-xs font-bold uppercase tracking-[0.2em] mb-2">Total Profit Trade</p>
            <h3 className="text-4xl font-bold text-white tracking-tighter">
              ${financialStats.totalProfitTrade.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </h3>
            <div className="mt-4 flex items-center gap-2 text-emerald-500 text-xs font-bold">
              <CheckCircle2 size={14} />
              <span>Available for settlement</span>
            </div>
          </div>
        </div>
        */}

        <div className="bg-brand-card border border-brand-primary/20 p-8 rounded-3xl relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
            <ShieldCheck size={80} className="text-brand-primary" />
          </div>
          <div className="relative z-10">
            <p className="text-brand-muted text-xs font-bold uppercase tracking-[0.2em] mb-2">Withdrawal Allowance</p>
            <h3 className="text-4xl font-bold text-brand-primary tracking-tighter">
              ${withdrawalAllowance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </h3>
            <p className="mt-4 text-brand-muted text-[10px] leading-relaxed max-w-[280px] uppercase font-bold tracking-tighter opacity-70">
              Formula: Total Profit Trade - (Total Loss Trade + Total Approved Withdrawals).
              Limits based on net realized equity within the Nexus protocols.
            </p>
          </div>
        </div>
      </section>

      {/* Info Note */}
      <div className="bg-brand-primary/5 border border-brand-primary/10 p-4 rounded-2xl flex items-start gap-4">
        <div className="bg-brand-primary/10 p-2 rounded-xl">
          <Info size={20} className="text-brand-primary shrink-0" />
        </div>
        <p className="text-brand-muted text-[11px] leading-relaxed italic uppercase font-bold tracking-wider opacity-80">
          Please verify your BTC destination address twice. COINNEXS operates as an non-custodial gateway; once redemption is initiated on the blockchain, it cannot be reversed.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">

        {/* 2. Withdrawal Request Form */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-brand-card border border-brand-border rounded-3xl p-8 shadow-xl">
            <h2 className="text-xl font-bold text-white mb-6 uppercase tracking-tight flex items-center gap-2 italic">
              <ArrowUpRight size={20} className="text-brand-primary" />
              Initiate Redemption
            </h2>

            {successMessage && (
              <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center gap-3 animate-in zoom-in duration-300">
                <CheckCircle2 size={18} className="text-emerald-500" />
                <p className="text-emerald-500 text-[11px] font-bold uppercase tracking-wider">{successMessage}</p>
              </div>
            )}

            <form onSubmit={handleWithdrawal} className="space-y-6">
              <div className="space-y-4">
                <div className="relative">
                  <Input
                    label="Withdrawal Quantity (USD)"
                    placeholder="0.00"
                    type="number"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="pr-16"
                  />
                  <div className="absolute top-10 right-4 text-[10px] font-bold text-brand-muted uppercase">USD</div>
                  <div className="mt-2 flex justify-between items-center px-1">
                    <span className="text-[10px] text-brand-muted uppercase font-black tracking-widest opacity-60">Available Limit</span>
                    <span className={`text-[10px] font-black tracking-widest ${parseFloat(amount) > withdrawalAllowance ? 'text-red-500' : 'text-brand-primary'}`}>
                      ${withdrawalAllowance.toLocaleString()}
                    </span>
                  </div>
                </div>

                <Input
                  label="BTC Settlement Address"
                  placeholder="bc1q..."
                  value={btcAddress}
                  onChange={(e) => setBtcAddress(e.target.value)}
                  error={btcAddress && !validateBTC(btcAddress) ? 'Invalid BTC protocol format' : undefined}
                />
              </div>

              <Button
                type="submit"
                className="w-full h-14 shadow-lg shadow-brand-primary/10 font-black"
                disabled={!isFormValid}
                isLoading={isSubmitting}
              >
                EXECUTE REDEMPTION
              </Button>

              <p className="text-center text-[9px] text-brand-muted uppercase tracking-[0.2em] leading-loose font-bold opacity-60">
                Transaction fee: <span className="text-white">0.0001 BTC</span> estimated <br />
                Network processing time: <span className="text-white">10-60 Minutes</span>
              </p>
            </form>
          </div>
        </div>

        {/* 3. Withdrawal Requests List */}
        <div className="lg:col-span-7 space-y-6">
          <div className="flex items-center justify-between px-2">
            <div className="flex items-center gap-4">
              <h2 className="text-sm font-black text-brand-muted uppercase tracking-[0.2em] flex items-center gap-2">
                <History size={16} />
                Redemption Ledger
              </h2>
              <div className="hidden sm:block">
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                  className="bg-brand-dark border border-brand-border text-[9px] font-black uppercase tracking-widest text-brand-muted rounded-lg px-3 py-1.5 focus:outline-none focus:border-brand-primary transition-all"
                >
                  <option value="">All Protocols</option>
                  <option value="PENDING">Pending</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>
            </div>
            <span className="text-[10px] text-brand-muted font-mono bg-brand-dark px-2 py-1 rounded border border-brand-border">{requests.length} Records</span>
          </div>

          <div className="sm:hidden px-2">
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="w-full bg-brand-dark border border-brand-border text-[9px] font-black uppercase tracking-widest text-brand-muted rounded-xl px-4 py-3 focus:outline-none focus:border-brand-primary transition-all"
            >
              <option value="">All Protocols</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block bg-brand-card border border-brand-border rounded-3xl overflow-hidden shadow-2xl relative min-h-[300px]">
            {isLoading && (
              <div className="absolute inset-0 bg-brand-dark/20 backdrop-blur-[1px] z-10 flex items-center justify-center">
                <Loader2 className="animate-spin text-brand-primary" size={24} />
              </div>
            )}
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-brand-dark/40 border-b border-brand-border">
                  <th className="px-6 py-4 text-[10px] font-black text-brand-muted uppercase tracking-[0.2em]">Hash / Date</th>
                  <th className="px-6 py-4 text-[10px] font-black text-brand-muted uppercase tracking-[0.2em]">Amount</th>
                  <th className="px-6 py-4 text-[10px] font-black text-brand-muted uppercase tracking-[0.2em]">Protocol</th>
                  <th className="px-6 py-4 text-[10px] font-black text-brand-muted uppercase tracking-[0.2em]">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-border">
                {requests.length === 0 && !isLoading ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-20 text-center text-brand-muted text-[10px] font-black uppercase tracking-widest opacity-40">
                      No redemption activity found
                    </td>
                  </tr>
                ) : requests.map((req) => (
                  <tr key={req.id} className="hover:bg-brand-primary/5 transition-colors">
                    <td className="px-6 py-4">
                      <span className="text-xs text-brand-muted font-mono">
                        {req.id ? `${req.id.slice(0, 12)}...` : 'N/A'}
                      </span>
                      <p className="text-[9px] text-brand-muted/50 mt-1 uppercase font-bold">{new Date(req.requestedAt).toLocaleString()}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-bold text-white">${parseFloat(req.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-xs text-brand-muted font-mono" title={req.destinationAddress}>{maskAddress(req.destinationAddress)}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-md text-[9px] font-black border uppercase tracking-widest ${getStatusColor(req.status)}`}>
                        {req.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4 relative min-h-[200px]">
            {isLoading && (
              <div className="absolute inset-0 bg-brand-dark/20 backdrop-blur-[1px] z-10 flex items-center justify-center">
                <Loader2 className="animate-spin text-brand-primary" size={24} />
              </div>
            )}
            {requests.map((req) => (
              <div key={req.id} className="bg-brand-card border border-brand-border p-5 rounded-2xl space-y-4 animate-in slide-in-from-left-4 duration-500">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-[10px] text-brand-muted uppercase font-black tracking-widest mb-1 opacity-60">Asset Redemption</p>
                    <span className="text-lg font-bold text-white">${parseFloat(req.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  <span className={`px-2 py-1 rounded-md text-[9px] font-bold border uppercase tracking-widest ${getStatusColor(req.status)}`}>
                    {req.status}
                  </span>
                </div>
                <div className="flex items-center justify-between pt-4 border-t border-brand-border/30">
                  <div className="flex flex-col">
                    <span className="text-[9px] text-brand-muted uppercase font-black tracking-widest opacity-60">Protocol Address</span>
                    <span className="text-[11px] text-brand-muted font-mono">{maskAddress(req.destinationAddress)}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-[9px] text-brand-muted uppercase font-black tracking-widest opacity-60">Executed On</span>
                    <p className="text-[11px] text-brand-muted font-mono">{new Date(req.requestedAt).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {requests.length > 0 && (
            <div className="flex items-center justify-between px-2 pt-4">
              <span className="text-[10px] text-brand-muted font-black uppercase tracking-widest opacity-60">Page {page}</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1 || isLoading}
                  className="p-2 rounded-xl bg-brand-card border border-brand-border text-brand-muted hover:text-white disabled:opacity-30 transition-all font-black"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={requests.length < limit || isLoading}
                  className="p-2 rounded-xl bg-brand-card border border-brand-border text-brand-muted hover:text-white disabled:opacity-30 transition-all font-black"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

          {requests.length === 0 && !isLoading && (
            <div className="py-20 flex flex-col items-center justify-center text-center bg-brand-card/50 border border-dashed border-brand-border rounded-3xl">
              <AlertCircle size={32} className="text-brand-muted mb-3 opacity-30" />
              <p className="text-brand-muted text-[10px] font-black uppercase tracking-widest opacity-50">No activity recorded on the nexus</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Withdrawals;
